import { ActionIcon, Button, Menu } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconDots, IconHelp, IconPlus, IconReload } from '@tabler/icons-react';
import clsx from 'clsx';
import { type JSX } from 'react';
import type { RecorderCommand } from '~/common/recorder/recorder-operation';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import EditableTextArea from '../common/EditableTextArea';
import { WEB_RTC_OVERLAY_ZIndex } from './implementations/WebRtcVmView';

const ICON_SIZE = 15;

const NAVIGATION_BUTTONS = [
  {
    label: 'Back',
    action: 'goBack',
    icon: <IconArrowLeft size={ICON_SIZE} />,
  },
  {
    label: 'Forward',
    action: 'goForward',
    icon: <IconArrowRight size={ICON_SIZE} />,
  },
  {
    label: 'Reload',
    action: 'reload',
    icon: <IconReload size={ICON_SIZE} />,
  },
] satisfies { label: string; action: 'goBack' | 'goForward' | 'reload'; icon: JSX.Element }[];

export default function WebBrowserHeader({
  sendCommand,
  isMobile,
  activeTab,
}: {
  sendCommand?: (command: RecorderCommand) => void;
  isMobile: boolean;
  activeTab?: string;
}) {
  const { vmStatus, recorderInitiator } = useLiveStateManager();
  const actionDisabled = recorderInitiator?.executionType === 'preview' || vmStatus?.state !== 'ready';

  return (
    <div className="flex w-full flex-col rounded-t-md border-gray-400 bg-[#cfcfd0] px-1 dark:border-gray-600 dark:bg-[#444950]">
      <div>
        <div className="flex h-10 items-center justify-start space-x-4 px-2">
          {!isMobile && (
            <div className="flex flex-row gap-[0.375rem]">
              <div className="h-2 w-2 rounded-full bg-[rgb(242,95,88)]" />
              <div className="h-2 w-2 rounded-full bg-[rgb(251,190,60)]" />
              <div className="h-2 w-2 rounded-full bg-[rgb(88,203,66)]" />
            </div>
          )}

          {vmStatus && sendCommand ?
            <div className="animate-slowFadeIn flex w-full items-center space-x-2">
              {!isMobile && (
                <div className="flex items-center space-x-1">
                  {NAVIGATION_BUTTONS.map((button) => (
                    <ActionIcon
                      key={button.action}
                      variant="transparent"
                      color="gray"
                      size="xs"
                      disabled={actionDisabled}
                      onClick={() =>
                        sendCommand({
                          type: 'operation',
                          operation: { operationType: 'ACTION', action: button.action },
                        })
                      }
                    >
                      {button.icon}
                    </ActionIcon>
                  ))}
                </div>
              )}
              <div
                className={clsx(
                  'my-[1px] flex items-center gap-2 rounded-md bg-white px-3 py-[1px] text-[11px] text-stone-800 dark:bg-gray-200',
                  isMobile ? 'w-full' : 'w-2/3 xl:w-1/2',
                )}
              >
                <EditableTextArea
                  readonly={recorderInitiator?.executionType === 'preview'}
                  maxTextLength={40}
                  initialVal={activeTab ? (vmStatus?.aliasesToUrls?.[activeTab] ?? 'about:blank') : 'about:blank'}
                  placeHolder={activeTab ? (vmStatus?.aliasesToUrls?.[activeTab] ?? 'about:blank') : 'about:blank'}
                  handleSave={(val) => {
                    sendCommand({
                      type: 'operation',
                      operation: {
                        operationType: 'NAVIGATION',
                        value: val.startsWith('http') ? val : `http://${val}`,
                      },
                    });
                  }}
                  className="max-h-6 overflow-hidden"
                  textStyle="text-xs text-stone-700 break-all"
                  inputStyle=" text-xs text-stone-700 dark:focus:text-stone-700"
                />
              </div>

              {!isMobile && (
                <>
                  <div className="ml-auto flex-grow" />
                  <Button
                    title="Add tab"
                    leftSection={<IconPlus size={10} />}
                    disabled={actionDisabled}
                    onClick={() =>
                      sendCommand({
                        type: 'operation',
                        operation: { operationType: 'NAVIGATION', value: '', newPage: true },
                      })
                    }
                    size="compact-xs"
                    variant="default"
                  >
                    Tab
                  </Button>
                  <div
                    className="relative flex items-center justify-center"
                    style={{ zIndex: WEB_RTC_OVERLAY_ZIndex + 1 }}
                  >
                    <ActionIcon
                      title="Help"
                      variant="subtle"
                      size="sm"
                      radius="xl"
                      disabled
                    >
                      <IconHelp />
                    </ActionIcon>
                  </div>
                </>
              )}
            </div>
          : isMobile ?
            <div className="h-full w-full cursor-not-allowed py-1.5">
              <div className="h-full w-full rounded-md bg-white/50 dark:bg-gray-200/50" />
            </div>
          : null}
          {!!isMobile && (
            <Menu shadow="md">
              <Menu.Target>
                <ActionIcon
                  size="sm"
                  variant="transparent"
                  color="gray"
                  disabled={!(vmStatus && sendCommand) || actionDisabled}
                  className="disabled:bg-transparent"
                >
                  <IconDots />
                </ActionIcon>
              </Menu.Target>
              {!!vmStatus && !!sendCommand && (
                <Menu.Dropdown>
                  <div className="mb-1 flex w-full justify-around">
                    {NAVIGATION_BUTTONS.map((button) => (
                      <ActionIcon
                        key={button.action}
                        color="gray"
                        size="md"
                        disabled={actionDisabled}
                        title={button.label}
                        onClick={() =>
                          sendCommand({
                            type: 'operation',
                            operation: { operationType: 'ACTION', action: button.action },
                          })
                        }
                      >
                        {button.icon}
                      </ActionIcon>
                    ))}
                  </div>

                  <Menu.Item
                    disabled={actionDisabled}
                    leftSection={<IconPlus size={ICON_SIZE} />}
                    onClick={() =>
                      sendCommand({
                        type: 'operation',
                        operation: { operationType: 'NAVIGATION', value: '', newPage: true },
                      })
                    }
                  >
                    Add tab
                  </Menu.Item>

                  <Menu.Item
                    disabled
                    leftSection={<IconHelp size={ICON_SIZE} />}
                  >
                    Help
                  </Menu.Item>
                </Menu.Dropdown>
              )}
            </Menu>
          )}
        </div>
      </div>
    </div>
  );
}
